import { useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AvatarGroup } from '@material-ui/lab';
import { useModal } from 'react-modal-hook';

import MessageBase, { MessageBaseProps } from 'components/MessageBase';
import ModalAddContato from 'components/ModalAddContato';
import { ContatoInterface } from 'store/reducers/contatos';

import DialogHeader from './DialogHeader';
import MessageErrorFallback from './MessageErrorFallback';

type MultiContatoMessageProps = MessageBaseProps;

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    overflow: 'hidden',
  },
  avatar: {
    color: 'inherit',
  },
  dialog: {
    maxHeight: '75%',
  },
  listItem: {
    paddingBottom: spacing(1.5),
    paddingInline: spacing(1),
    cursor: 'pointer',
  },
  seeAllBtn: {
    padding: spacing(0.5),
  },
}));

export default function MultiContatoMessage(props: MultiContatoMessageProps) {
  const classes = useStyles();

  const [selectedContatos, setSelectedContatos] = useState<
    ContatoInterface[] | null
  >(null);

  const [showListaDeContatosModal, hideListaDeContatosModal] = useModal(
    ({ in: open }) => {
      if (!selectedContatos) return null;

      return (
        <ListaDeContatosModal
          open={open}
          onClose={hideListaDeContatosModal}
          contatos={selectedContatos}
        />
      );
    },
    [selectedContatos],
  );

  try {
    const { mensagem } = props;
    const contatos = mensagem?.mensagemContato as ContatoInterface[];

    const handleShowLista = () => {
      setSelectedContatos(contatos);
      showListaDeContatosModal();
    };

    return (
      <MessageBase maxWidth="50%" {...props}>
        <Box className={classes.root}>
          <ListItem
            component="div"
            divider
            disableGutters
            dense
            className={classes.listItem}
            onClick={handleShowLista}
          >
            <ListItemAvatar>
              <AvatarGroup spacing={20}>
                {contatos.slice(0, 3).map(contato => (
                  <Avatar
                    key={contato.contato}
                    src={contato.foto}
                    className={classes.avatar}
                  />
                ))}
              </AvatarGroup>
            </ListItemAvatar>

            <Typography
              variant="body2"
              style={{ fontWeight: 500, marginLeft: 8 }}
              noWrap
            >
              {contatos[0]?.nome}
              {contatos.length === 2 && ' e 1 outro contato'}
              {contatos.length > 2 &&
                ` e outros ${contatos.length - 1} contatos`}
            </Typography>
          </ListItem>

          <ListItem component="div" disableGutters dense>
            <Button
              onClick={handleShowLista}
              size="small"
              color="inherit"
              fullWidth
              className={classes.seeAllBtn}
            >
              Ver todos
            </Button>
          </ListItem>
        </Box>
      </MessageBase>
    );
  } catch (error) {
    return <MessageErrorFallback {...props} />;
  }
}

interface ListaDeContatosModalProps {
  open: boolean;
  onClose: () => void;
  contatos: ContatoInterface[];
}

function ListaDeContatosModal(props: ListaDeContatosModalProps) {
  const { open, onClose, contatos } = props;
  const classes = useStyles();
  const [mensagemContato, setMensagemContato] = useState<ContatoInterface>();

  const [showModalAddContato, hideModalAddContato] = useModal(
    ({ in: open }) => {
      if (!mensagemContato) return null;
      return (
        <ModalAddContato
          open={open}
          contato={mensagemContato}
          onClose={hideModalAddContato}
        />
      );
    },
    [mensagemContato],
  );

  const handleAddContato = (contato: ContatoInterface) => {
    setMensagemContato(contato);
    showModalAddContato();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      classes={{ paper: classes.dialog }}
    >
      <DialogHeader title="Contatos" onClose={onClose} />

      <DialogContent>
        {contatos.map((contato, index) => (
          <ListItem
            key={contato.contato}
            divider={index !== contatos.length - 1}
            dense
            style={{
              paddingInline: 4,
              ...(index === 0 ? { paddingBottom: 16 } : { paddingBlock: 16 }),
            }}
          >
            <ListItemAvatar>
              <Avatar src={contato.foto} />
            </ListItemAvatar>

            <ListItemText
              primary={contato.nome}
              secondary={contato.contato}
              primaryTypographyProps={{ noWrap: true }}
              secondaryTypographyProps={{
                noWrap: true,
                component: 'p',
                variant: 'caption',
                style: { opacity: 0.85 },
              }}
            />

            <Button
              onClick={() => handleAddContato(contato)}
              size="small"
              color="primary"
              variant="contained"
            >
              Adicionar
            </Button>
          </ListItem>
        ))}
      </DialogContent>
    </Dialog>
  );
}
